import { nodeToText } from "@corca/core";

import { mergeInlineEditablesInNode } from "@/components/input/utils/mergeInlineEditables";

import { ComputedResultNodeModel } from "../nodes/ComputedResultNodeModel";
import { GroupNodeModel } from "../nodes/GroupNodeModel";
import { InlineNodeModel } from "../nodes/InlineNodeModel";
import { WrapperNodeModel } from "../nodes/WrapperNodeModel";

import {
  countEqualsSigns,
  getComputedResultNode,
  getRhsExtras,
  isStandaloneComputedResult,
  referenceNeedsBrackets,
  syncReferenceBrackets,
} from "./utils";

import type { ComputedReferenceNodeModel } from "../nodes/ComputedReferenceNodeModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeId } from "@corca/data";

/**
 * Single-writer maintenance: disable the computed flag when the "=" structure
 * breaks or the user typed alongside the result, unwrapping the result back into
 * plain editable nodes.
 */
export function maintainComputedStructure(doc: DocumentModel): boolean {
  let changed = false;
  const ops: (() => void)[] = [];
  const postOps: (() => void)[] = [];

  for (const wrapper of doc.textNode.wrapperNodes) {
    if (!wrapper.computed) continue;

    // A computed wrapper always owns a result node (created on enable).
    const result = getComputedResultNode(wrapper);
    if (!result) {
      collectUnwrapResultsOps(doc, wrapper, ops, postOps);
      changed = true;
      continue;
    }

    if (!isStandaloneComputedResult(wrapper)) {
      // Equation shape: a broken/duplicated "=" or content typed alongside the result on the RHS
      // is no longer a valid computed equation.
      if (countEqualsSigns(wrapper.inlineNode?.nodes ?? []) !== 1) {
        collectUnwrapResultsOps(doc, wrapper, ops, postOps);
        changed = true;
        continue;
      }
      if (getRhsExtras(wrapper).length > 0) {
        collectUnwrapResultsOps(doc, wrapper, ops, postOps);
        changed = true;
        continue;
      }
    }

    const content = nodeToText(result.toJSON());
    if (result.props.snapshot === undefined) {
      // First sight without a stamped snapshot — accept current content as the baseline.
      ops.push(() => {
        result.updateProps({ snapshot: content });
      });
    } else if (result.props.snapshot !== content) {
      collectUnwrapResultsOps(doc, wrapper, ops, postOps);
      changed = true;
    }
  }

  // De-reference any reference node whose content drifted from the last synced
  // snapshot — this mirrors the snapshot check above for ComputedResult.
  for (const ref of doc.getNodesByType("computedReference")) {
    const content = nodeToText(ref.toJSON());
    if (ref.props.snapshot === undefined) {
      ops.push(() => {
        ref.updateProps({ snapshot: content });
      });
    } else if (ref.props.snapshot !== content) {
      collectUnwrapReferenceOps(doc, ref, ops, postOps);
      changed = true;
      continue;
    }

    if (!!ref.props.bracketed !== referenceNeedsBrackets(ref)) {
      ops.push(() => syncReferenceBrackets(ref));
    }
  }

  if (ops.length > 0) {
    doc.transact(
      () => {
        ops.forEach((op) => op());
      },
      `ComputedNodeManager:disable`,
      { runInAction: true, skipComputedRecalc: true, preventNewUndoStack: true },
    );
    postOps.forEach((op) => op());
  }

  return changed;
}

/**
 * Swap `node` in `parent` for `replacementIds`, returning where it sat. The node is looked up when
 * the op runs: an earlier unwrap in the same pass may have shifted it.
 */
function replaceChild(parent: InlineNodeModel, node: NodeModel, replacementIds: NodeId[]): number {
  const index = parent.childrenIds.indexOf(node.id);
  if (index !== -1) {
    const before = parent.childrenIds.slice(0, index);
    const after = parent.childrenIds.slice(index + 1);
    parent.setChildren([...before, ...replacementIds, ...after]);
  }
  return index;
}

/**
 * Tracks where an unwrapped node's replacement ends up, so the caret can be placed against it once
 * every op of the pass has run.
 */
class Unwrapping {
  replacementIds: NodeId[];

  private index = -1;

  constructor(
    private _parent: InlineNodeModel,
    private node: NodeModel,
    replacementIds: NodeId[],
  ) {
    this.replacementIds = replacementIds;
  }

  get parent(): InlineNodeModel {
    return this._parent;
  }

  apply(): void {
    // An earlier unwrap in the same pass may have moved the node, e.g. a reference out of a result.
    const { parent } = this.node;
    if (parent?.is(InlineNodeModel)) this._parent = parent;

    this.index = replaceChild(this._parent, this.node, this.replacementIds);
  }

  /** The caret index after the first `count` replacement nodes (all of them by default). */
  caretIndexAfter(count = this.replacementIds.length): number {
    const { childrenIds } = this._parent;
    if (this.replacementIds.length === 0) return this.index;
    if (count === 0) return childrenIds.indexOf(this.replacementIds[0]);

    const last = childrenIds.indexOf(this.replacementIds[count - 1]);
    return last === -1 ? -1 : last + 1;
  }
}

/**
 * Queue the caret move for an unwrap. The cursor is anchored to the node before it, so it follows
 * any shift by itself unless it sits inside the unwrapped node or right after it.
 */
function collectCaretOps(
  doc: DocumentModel,
  unwrapping: Unwrapping,
  parent: InlineNodeModel,
  node: ComputedResultNodeModel | ComputedReferenceNodeModel,
  postOps: (() => void)[],
): void {
  const { cursor } = doc;
  let count: number | undefined;
  if (cursor.node && cursor.node === node.inlineNode) {
    count = cursor.index ?? 0;
  } else if (cursor.node !== parent || cursor.indexNodeId !== node.id) {
    return;
  }

  postOps.push(() => {
    const index = unwrapping.caretIndexAfter(count);
    if (index !== -1) cursor.moveTo(unwrapping.parent, index);
  });
}

export function collectUnwrapResultsOps(
  doc: DocumentModel,
  node: WrapperNodeModel | InlineNodeModel,
  ops: (() => void)[],
  postOps: (() => void)[],
): void {
  const inlineNode = node.is(InlineNodeModel) ? node : node.inlineNode;
  const { cursor } = doc;
  const results = inlineNode?.nodes.filter((n) => n.is(ComputedResultNodeModel)) ?? [];
  const wrapper = node.is(WrapperNodeModel) ? node : node.parent;
  ops.push(() => {
    if (wrapper?.is(WrapperNodeModel)) {
      wrapper.updateProps({ computed: false });
    }
  });

  if (!inlineNode?.is(InlineNodeModel)) return;

  for (const result of results) {
    const innerIds = result.inlineNode ? [...result.inlineNode.childrenIds] : [];
    const unwrapping = new Unwrapping(inlineNode, result, innerIds);

    collectCaretOps(doc, unwrapping, inlineNode, result, postOps);
    ops.push(() => unwrapping.apply());
    postOps.push(() => {
      mergeInlineEditablesInNode(doc, cursor.node);
    });
  }
}

/**
 * Replace a reference node with its cached content (plain editable nodes),
 * removing the reference relationship. Runs single-writer in `maintainStructure`.
 */
export function collectUnwrapReferenceOps(
  doc: DocumentModel,
  ref: ComputedReferenceNodeModel,
  ops: (() => void)[],
  postOps: (() => void)[],
): void {
  const parentInline = ref.parent;
  if (!parentInline?.is(InlineNodeModel)) return;

  const refInline = ref.inlineNode;
  const unwrapping = new Unwrapping(parentInline, ref, refInline ? [...refInline.childrenIds] : []);
  const keepBrackets = !!refInline && referenceNeedsBrackets(ref);

  // The group takes over the reference's own inline node, so a caret inside keeps its place.
  if (!(keepBrackets && doc.cursor.node === refInline)) {
    collectCaretOps(doc, unwrapping, parentInline, ref, postOps);
  }

  ops.push(() => {
    if (keepBrackets) {
      unwrapping.replacementIds = [doc.create(GroupNodeModel, [refInline], "(", ")").id];
    }
    unwrapping.apply();
  });
}
